import type { SettlementItem } from '../Module/Settlement/Domain/entity/SettlementTypes';

/** 프로젝트 원 단위(raw) → 프로젝트 표준 금액 (USD는 API mille 단위 → 실제 달러) */
export function projectRawToBaseUnits(rawAmount: number, projectCurrencyIsUsd: boolean): number {
  return projectCurrencyIsUsd ? rawAmount / 1000 : rawAmount;
}

export function resolveNeedsFxConversion(displayAsKrw: boolean, projectCurrencyIsUsd: boolean): boolean {
  return (displayAsKrw && projectCurrencyIsUsd) || (!displayAsKrw && !projectCurrencyIsUsd);
}

/** 환율은 통상 1 USD당 KRW 금액으로 가정 */
export function convertProjectBaseWithFx(
  projectBase: number,
  options: {
    needsFxConversion: boolean;
    displayAsKrw: boolean;
    projectCurrencyIsUsd: boolean;
    exchangeRate?: number | null;
  },
): number {
  const { needsFxConversion, displayAsKrw, projectCurrencyIsUsd, exchangeRate } = options;
  if (!needsFxConversion) return projectBase;
  const rate = exchangeRate;
  if (rate == null || !Number.isFinite(rate) || rate <= 0) return projectBase;
  if (displayAsKrw && projectCurrencyIsUsd) return projectBase * rate;
  if (!displayAsKrw && !projectCurrencyIsUsd) return projectBase / rate;
  return projectBase;
}

export function roundDisplayAmount(value: number, currency: 'USD' | 'KRW'): number {
  if (!Number.isFinite(value)) return 0;
  return currency === 'KRW' ? Math.round(value) : Math.round(value * 100) / 100;
}

export function pickSettlementItemExchangeRate(item: SettlementItem): number | undefined {
  const r = item.exchangeRate ?? item.exchange_rate;
  if (r == null) return undefined;
  const n = typeof r === 'string' ? Number(String(r).trim()) : Number(r);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n;
}

/** 여러 정산 행 중 유효 환율이 있으면 가장 최근(연·월 최대) 행의 환율 */
export function pickLatestSettlementExchangeRate(items: SettlementItem[]): number | undefined {
  if (!items.length) return undefined;
  const sorted = [...items].sort((a, b) => {
    if (a.settle_year !== b.settle_year) return b.settle_year - a.settle_year;
    return b.settle_month - a.settle_month;
  });
  for (const row of sorted) {
    const rate = pickSettlementItemExchangeRate(row);
    if (rate !== undefined) return rate;
  }
  return undefined;
}

export type PaymentDisplayResolution = { value: number; currency: 'USD' | 'KRW' };

/** 이미 프로젝트 기준 금액(달러 또는 원)인 값을 표시 통화로 변환 */
export function resolveProjectBaseForDisplay(
  projectBase: number,
  ctx: {
    displayAsKrw: boolean;
    projectCurrencyIsUsd: boolean;
    exchangeRate?: number | null;
  },
): PaymentDisplayResolution {
  const needsFx = resolveNeedsFxConversion(ctx.displayAsKrw, ctx.projectCurrencyIsUsd);
  const rate = ctx.exchangeRate;
  const rateOk = rate != null && Number.isFinite(rate) && rate > 0;

  let numeric = projectBase;
  if (needsFx && rateOk) {
    numeric = convertProjectBaseWithFx(projectBase, {
      needsFxConversion: true,
      displayAsKrw: ctx.displayAsKrw,
      projectCurrencyIsUsd: ctx.projectCurrencyIsUsd,
      exchangeRate: rate,
    });
  }

  const currency: 'USD' | 'KRW' =
    needsFx && !rateOk
      ? ctx.projectCurrencyIsUsd
        ? 'USD'
        : 'KRW'
      : ctx.displayAsKrw
        ? 'KRW'
        : 'USD';

  return { value: roundDisplayAmount(numeric, currency), currency };
}

/**
 * 단일 raw 금액을 결제현황 표시 금액으로 변환 (약정·상세 행 등).
 * 환율이 필요하지만 없으면 프로젝트 통화로 표시하는 편이 오표시(원 기호에 달러 값)보다 낫다.
 */
export function resolveSinglePaymentDisplay(
  rawAmount: number,
  ctx: {
    projectCurrencyIsUsd: boolean;
    displayAsKrw: boolean;
    exchangeRate?: number | null;
  },
): PaymentDisplayResolution {
  const projectBase = projectRawToBaseUnits(rawAmount, ctx.projectCurrencyIsUsd);
  const needsFx = resolveNeedsFxConversion(ctx.displayAsKrw, ctx.projectCurrencyIsUsd);
  const rate = ctx.exchangeRate;
  const rateOk = rate != null && Number.isFinite(rate) && rate > 0;

  let numeric = projectBase;
  if (needsFx && rateOk) {
    numeric = convertProjectBaseWithFx(projectBase, {
      needsFxConversion: true,
      displayAsKrw: ctx.displayAsKrw,
      projectCurrencyIsUsd: ctx.projectCurrencyIsUsd,
      exchangeRate: rate,
    });
  }

  const currency: 'USD' | 'KRW' =
    needsFx && !rateOk
      ? ctx.projectCurrencyIsUsd
        ? 'USD'
        : 'KRW'
      : ctx.displayAsKrw
        ? 'KRW'
        : 'USD';

  return { value: roundDisplayAmount(numeric, currency), currency };
}
